/**
 * Ring buffer of recent console output, persisted across app restarts.
 *
 * Two consumers:
 *  - Crashlytics, which attaches the recent tail to every report as breadcrumbs.
 *    Our JS stacks are minified and the Crashlytics Gradle plugin only deobfuscates
 *    native frames, so the run-up to a crash is often more useful than the stack.
 *  - A "copy logs" affordance for field support, which is the only diagnostic
 *    available on web, where Crashlytics does not exist.
 *
 * Two tiers, deliberately:
 *  - The session ring holds ALL levels and lives only in memory. Measured on a real
 *    cold start this app emits ~36 lines, none of them warn/error, so a warn/error
 *    filter would leave a healthy session's buffer empty — the [log] lines are what
 *    actually explain a bug (the OTA 204 defect was diagnosed purely from them).
 *  - The problem ring holds only warn/error and is the only tier written to disk, so
 *    routine chatter can never dilute or evict the long-term failure record.
 *
 * The cost of that split is losing info-level context from before a restart. It is
 * an acceptable one: for a crash, the run-up was already captured into the Crashlytics
 * report at failure time, and for a still-running app the current session is the
 * relevant window.
 *
 * Persistence uses localStorage rather than Filesystem/Preferences: the WebView's
 * origin store already survives app restarts and OTA bundle swaps, it is synchronous
 * (so it can be flushed during pagehide, when async APIs are not guaranteed to
 * finish), and it needs no native plugin — meaning this ships over OTA instead of
 * requiring a new APK. The same choice is already made in src/ota/utils.ts.
 *
 * Both tiers are bounded on entry count AND total bytes, evicting oldest-first.
 */

const PROBLEMS_STORAGE_KEY = 'dr.diagnostics.problems.v1'
const MAX_ENTRIES = 500
// Warnings and errors are also kept in their own, separately-capped ring.
//
// Measured on a real cold start, this app emits ~36 lines of which zero were warn or
// error — the routine [info]/[log] chatter (Capgo alone emits ~15 per start) would
// evict a genuine failure from the main ring long before a driver phones support.
// Retaining problems separately means the main ring can stay full-fidelity for the
// run-up while the failures themselves survive for far longer.
const MAX_PROBLEM_ENTRIES = 150
const PROBLEM_LEVELS: LogLevel[] = ['warn', 'error']
const MAX_LINE_LENGTH = 500
// Upper bound on the string handed to the redaction regexes — see formatArguments.
const REDACTION_WINDOW = MAX_LINE_LENGTH * 4
// Bounds the in-memory ring's RAM as well as its entry count, since one pathological
// log line should not be able to inflate it.
const MAX_BUFFER_BYTES = 192 * 1024
// Hard ceiling on what reaches disk. Only problems are durable, so this is the whole
// storage budget: trivial next to any app's data allowance and far inside the ~5 MB
// localStorage origin quota.
const MAX_PERSISTED_BYTES = 96 * 1024
// Writing on every console call would serialise the whole buffer hundreds of times a
// minute; batch instead, with an unconditional flush when the app backgrounds.
const PERSIST_THROTTLE_MS = 5000

/**
 * Secrets and personal data are scrubbed as lines enter the buffer, not as they
 * leave it. Both consumers are exposures — the buffer is uploaded to Crashlytics
 * and pasted into support chats — and redacting once, at the entry point, means
 * neither can leak something the other filtered. It also keeps the plaintext out
 * of memory entirely.
 *
 * Verified against real device output: the app logs FCM registration tokens
 * (`[Push] Registration successful, token: …`) and Capgo device UUIDs.
 */
const REDACTIONS: Array<[RegExp, string]> = [
  // FCM registration token: "<instance-id>:APA91b<payload>"
  [/[\w-]{8,}:APA91b[\w-]+/g, '<fcm-token>'],
  // JWTs, with or without a Bearer prefix
  [/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, '<jwt>'],
  // key/value secrets in either JSON or "key: value" form
  [
    /("?\b(?:access_?token|api_?key|authorization|password|passwd|secret|token)\b"?\s*[:=]\s*"?)([^",\s}]+)/gi,
    '$1<redacted>',
  ],
  [/[\w.+-]+@[\w-]+\.[\w.]+/g, '<email>'],
  // Device / installation UUIDs
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<uuid>'],
]

const redact = (line: string): string =>
  REDACTIONS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), line)

export type LogLevel = 'log' | 'info' | 'warn' | 'error'

export interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
}

const TEED_LEVELS: LogLevel[] = ['log', 'info', 'warn', 'error']

const buffer: LogEntry[] = []
const problems: LogEntry[] = []
let installed = false
// Running total so the byte cap costs O(1) per write instead of re-serialising the
// whole buffer on every console call.
let bufferBytes = 0
let dirty = false
let persistTimer: ReturnType<typeof setTimeout> | null = null

// Approximate serialised cost of one entry: the fields plus JSON punctuation/keys.
const entrySize = (entry: LogEntry): number =>
  entry.message.length + entry.level.length + entry.timestamp.length + 48

const evictToCaps = (): void => {
  while (buffer.length > MAX_ENTRIES || bufferBytes > MAX_BUFFER_BYTES) {
    const removed = buffer.shift()
    if (!removed) {
      bufferBytes = 0
      break
    }
    bufferBytes -= entrySize(removed)
  }
}

// The durable ring is capped on entries and on bytes, so it can never grow into a
// device-storage problem no matter how verbose an individual failure is.
const evictProblemsToCaps = (): void => {
  let bytes = problems.reduce((total, entry) => total + entrySize(entry), 0)

  while (problems.length > MAX_PROBLEM_ENTRIES || bytes > MAX_PERSISTED_BYTES) {
    const removed = problems.shift()
    if (!removed) {
      break
    }
    bytes -= entrySize(removed)
  }
}

const isLogEntry = (value: unknown): value is LogEntry => {
  const entry = value as LogEntry
  return (
    !!entry &&
    typeof entry.timestamp === 'string' &&
    typeof entry.message === 'string' &&
    TEED_LEVELS.includes(entry.level)
  )
}

// Only problems are durable. The full-fidelity ring stays in memory for the current
// session: its run-up value is already preserved where it matters (Crashlytics
// breadcrumbs are attached at failure time), and keeping it off disk is what stops
// routine [info] chatter from diluting the long-term failure record.
const writeStorage = (): void => {
  localStorage.setItem(PROBLEMS_STORAGE_KEY, JSON.stringify(problems))
}

const persistNow = (): void => {
  if (!dirty || typeof localStorage === 'undefined') {
    return
  }
  dirty = false

  try {
    writeStorage()
  } catch {
    // Quota exceeded or storage disabled. Shed half of the ring that writeStorage
    // actually persists — shedding the in-memory one would destroy the session's
    // run-up while leaving the failing write completely unchanged.
    try {
      problems.splice(0, Math.floor(problems.length / 2))
      writeStorage()
    } catch {
      // Best effort only — both rings keep working in memory regardless.
    }
  }
}

const schedulePersist = (): void => {
  dirty = true
  if (persistTimer) {
    return
  }
  persistTimer = setTimeout(() => {
    persistTimer = null
    persistNow()
  }, PERSIST_THROTTLE_MS)
}

/**
 * Writes any pending entries immediately. Called when the app backgrounds, where a
 * throttled write would otherwise be lost if the process is killed.
 */
export const flushLogBuffer = (): void => {
  if (persistTimer) {
    clearTimeout(persistTimer)
    persistTimer = null
  }
  persistNow()
}

const restore = (): void => {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    // Validate rather than trust: storage can be corrupt, truncated, or written by
    // an older build with a different entry shape.
    const raw = localStorage.getItem(PROBLEMS_STORAGE_KEY)
    if (!raw) {
      return
    }

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return
    }

    parsed.filter(isLogEntry).forEach(entry => problems.push(entry))
    evictProblemsToCaps()
  } catch {
    // Unparseable history is simply dropped.
  }
}

const formatArgument = (argument: unknown): string => {
  if (typeof argument === 'string') {
    return argument
  }

  if (argument instanceof Error) {
    return `${argument.name}: ${argument.message}`
  }

  try {
    return JSON.stringify(argument) ?? String(argument)
  } catch {
    // Circular structures, getters that throw, huge DOM nodes.
    return String(argument)
  }
}

const formatArguments = (args: unknown[]): string => {
  const joined = args.map(formatArgument).join(' ')

  // Bound the input to the redaction regexes. One console.log of a large object would
  // otherwise sweep five patterns across megabytes on the main thread, only for the
  // result to be truncated to MAX_LINE_LENGTH anyway. The window is deliberately far
  // wider than that limit so a secret straddling the truncation point is still matched
  // in full and replaced, rather than half-kept as a fragment.
  const bounded = joined.length > REDACTION_WINDOW ? joined.slice(0, REDACTION_WINDOW) : joined
  const line = redact(bounded)

  return line.length > MAX_LINE_LENGTH ? `${line.slice(0, MAX_LINE_LENGTH - 1)}…` : line
}

const push = (level: LogLevel, args: unknown[]): void => {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message: formatArguments(args),
  }

  buffer.push(entry)
  bufferBytes += entrySize(entry)
  evictToCaps()

  // Only a problem changes durable state, so only a problem schedules a write. Info
  // and log lines never touch disk at all.
  if (PROBLEM_LEVELS.includes(level)) {
    problems.push(entry)
    evictProblemsToCaps()
    schedulePersist()
  }
}

/**
 * Tees console output into the buffer. The original console methods still run, so
 * logcat / devtools output is unchanged. Safe to call more than once.
 */
export const installLogBuffer = (): void => {
  if (installed) {
    return
  }
  installed = true

  restore()

  // Timestamps the start of this session. Not persisted — the session ring always
  // begins here by definition; the value is knowing *when* it began when reading
  // logs alongside the durable problem history, which reaches further back.
  const marker: LogEntry = {
    timestamp: new Date().toISOString(),
    level: 'info',
    message: '───── app start ─────',
  }
  buffer.push(marker)
  bufferBytes += entrySize(marker)
  evictToCaps()

  // pagehide/visibilitychange are the reliable mobile lifecycle signals; beforeunload
  // is not dispatched dependably when a WebView is backgrounded or killed.
  window.addEventListener('pagehide', flushLogBuffer)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushLogBuffer()
    }
  })

  TEED_LEVELS.forEach(level => {
    const original = console[level].bind(console)

    console[level] = (...args: unknown[]): void => {
      // Buffering must never be able to break logging, or turn a logged error into
      // a thrown one that window.onerror re-reports in a loop.
      try {
        push(level, args)
      } catch {
        // Intentionally silent — there is nowhere safe to report this.
      }

      original(...args)
    }
  })
}

/**
 * A copy of the buffered entries, oldest first.
 */
export const getLogBuffer = (): LogEntry[] => [...buffer]

const formatEntry = (entry: LogEntry): string =>
  `${entry.timestamp} [${entry.level}] ${entry.message}`

/**
 * The whole buffer as plain text, oldest first — for the copy-to-clipboard support
 * flow, which wants the full retained history including previous app runs.
 */
export const formatLogBuffer = (): string => {
  const recent = buffer.map(formatEntry).join('\n')

  if (!problems.length) {
    return recent
  }

  // Problems first: whoever reads this is looking for what went wrong, and these
  // reach much further back than the full-fidelity ring below.
  return [
    `===== problems (${problems.length}, retained across restarts) =====`,
    problems.map(formatEntry).join('\n'),
    '',
    '===== recent activity =====',
    recent,
  ].join('\n')
}

/**
 * The most recent entries, oldest first, capped at `maxChars`.
 *
 * Crashlytics only accepts a bounded log section per report, and now that the buffer
 * survives restarts it can hold far more than that. Building from the tail avoids
 * rendering the entire history into a string on every single error just to slice the
 * end off it.
 */
export const formatRecentLogs = (maxChars: number): string => {
  const lines: string[] = []
  let total = 0

  for (let index = buffer.length - 1; index >= 0; index--) {
    const line = formatEntry(buffer[index])
    // +1 for the newline that will join it.
    if (total + line.length + 1 > maxChars) {
      break
    }
    lines.push(line)
    total += line.length + 1
  }

  return lines.reverse().join('\n')
}

export const clearLogBuffer = (): void => {
  buffer.length = 0
  problems.length = 0
  bufferBytes = 0

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(PROBLEMS_STORAGE_KEY)
    }
  } catch {
    // Nothing useful to do if storage refuses.
  }
}
